import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Play, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { SubscribersSubNav } from '@/components/admin/SubscribersSubNav';
import {
  AutomationConditionRows,
  isConditionComplete,
  type DraftCondition,
} from '@/components/admin/AutomationConditionRows';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Toggle } from '@/components/ui/Toggle';
import { adminAutomationsService } from '@/services/admin-automations.service';
import { adminEmailTemplatesService } from '@/services/admin-email-templates.service';
import { adminSubscriberListsService } from '@/services/admin-subscriber-lists.service';
import { ApiError } from '@/services/http';
import { timezoneOptions } from '@/lib/timezones';
import { BroadcastStatusChip } from './AdminBroadcasts';
import type {
  AudiencePreview,
  AutomationDetailView,
  ConditionSchemaView,
  EmailTemplateView,
  SubscriberListView,
} from '@/types/api';

const inputClass =
  'h-9 rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] font-normal normal-case text-or-slate-900 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30';

const labelClass =
  'flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500';

let uidSeed = 10_000;

export function AdminEditAutomationPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const [source, setSource] = useState<AutomationDetailView | null>(null);
  const [schema, setSchema] = useState<ConditionSchemaView | null>(null);
  const [lists, setLists] = useState<SubscriberListView[]>([]);
  const [templates, setTemplates] = useState<EmailTemplateView[]>([]);

  // Draft mirrors `source` until the admin edits; `dirty` drives the Save CTA.
  const [name, setName] = useState('');
  const [listId, setListId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [sendHour, setSendHour] = useState(9);
  const [timezone, setTimezone] = useState('America/Chicago');
  const [includeNoAccount, setIncludeNoAccount] = useState(false);
  const [maxPerRun, setMaxPerRun] = useState(500);
  const [conditions, setConditions] = useState<DraftCondition[]>([]);
  const [dirty, setDirty] = useState(false);

  const [preview, setPreview] = useState<AudiencePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  /** Bumped by Save to force the audience count to refresh. */
  const [previewNonce, setPreviewNonce] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const hydrate = useCallback((row: AutomationDetailView) => {
    setSource(row);
    setName(row.name);
    setListId(row.listId ?? '');
    setTemplateId(row.templateId ?? '');
    setSendHour(row.sendHour);
    setTimezone(row.timezone);
    setIncludeNoAccount(row.includeSubscribersWithoutAccount);
    setMaxPerRun(row.maxRecipientsPerRun);
    setConditions(
      row.conditions.map((c) => ({
        uid: uidSeed++,
        field: c.field,
        operator: c.operator,
        values: c.values,
      })),
    );
    setDirty(false);
  }, []);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      adminAutomationsService.getById(id),
      adminAutomationsService.conditionSchema(),
      adminSubscriberListsService.list(),
      adminEmailTemplatesService.list(),
    ])
      .then(([row, sch, l, t]) => {
        hydrate(row);
        setSchema(sch);
        setLists(l);
        setTemplates(t);
      })
      .catch((err) =>
        toast.error(err instanceof ApiError ? err.message : 'Could not load automation.'),
      )
      .finally(() => setLoading(false));
  }, [id, hydrate]);

  /**
   * The interlock. An `account.*` condition can never match a subscriber with
   * no account — in either polarity — so combining the two would silently
   * match nobody from the group the toggle claims to include. The server
   * rejects it; the UI refuses to build it in the first place.
   */
  const accountFields = useMemo(
    () => new Set((schema?.fields ?? []).filter((f) => f.requiresAccount).map((f) => f.name)),
    [schema],
  );
  const usesAccountFields = conditions.some((c) => accountFields.has(c.field));

  useEffect(() => {
    if (usesAccountFields && includeNoAccount) setIncludeNoAccount(false);
  }, [usesAccountFields, includeNoAccount]);

  const completeConditions = useMemo(
    () => conditions.filter(isConditionComplete),
    [conditions],
  );

  const markDirty = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setDirty(true);
  };

  // Live audience count, debounced so every keystroke in a day-count box
  // doesn't hit the database.
  //
  // `previewNonce` is bumped by Save so the count always refreshes on save,
  // even when the saved values are identical to what was last previewed and
  // nothing in the dependency list actually changed.
  const previewSeq = useRef(0);
  useEffect(() => {
    if (!id || loading) return;
    if (completeConditions.length === 0) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    const seq = ++previewSeq.current;
    setPreviewing(true);
    const timer = setTimeout(() => {
      adminAutomationsService
        .preview(id, {
          listId: listId || undefined,
          timezone,
          includeSubscribersWithoutAccount: includeNoAccount,
          maxRecipientsPerRun: maxPerRun > 0 ? maxPerRun : undefined,
          conditions: completeConditions.map((c) => ({
            field: c.field,
            operator: c.operator,
            values: c.values,
          })),
        })
        .then((res) => {
          // Ignore a slow response that a newer edit has superseded.
          if (seq !== previewSeq.current) return;
          setPreview(res);
          setPreviewError(null);
        })
        .catch((err) => {
          if (seq !== previewSeq.current) return;
          setPreview(null);
          setPreviewError(err instanceof ApiError ? err.message : 'Could not preview audience.');
        })
        .finally(() => {
          if (seq === previewSeq.current) setPreviewing(false);
        });
    }, 400);
    return () => clearTimeout(timer);
  }, [id, loading, listId, timezone, includeNoAccount, maxPerRun, completeConditions, previewNonce]);

  const handleSave = async () => {
    if (saving) return;
    const incomplete = conditions.length - completeConditions.length;
    if (incomplete > 0) {
      toast.error(`Finish or remove ${incomplete} incomplete rule(s) first.`);
      return;
    }
    setSaving(true);
    try {
      const updated = await adminAutomationsService.update(id, {
        name: name.trim(),
        listId: listId || undefined,
        templateId: templateId || undefined,
        sendHour,
        timezone,
        includeSubscribersWithoutAccount: includeNoAccount,
        maxRecipientsPerRun: maxPerRun,
        conditions: completeConditions.map((c) => ({
          field: c.field,
          operator: c.operator,
          values: c.values,
        })),
      });
      hydrate(updated);
      // Always re-count against what's now persisted, so the number on screen
      // is never left describing a draft that no longer exists.
      setPreviewNonce((n) => n + 1);
      toast.success('Automation saved.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save automation.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (next: boolean) => {
    if (dirty) {
      toast.error('Save your changes first.');
      return;
    }
    try {
      hydrate(await adminAutomationsService.setEnabled(id, next));
      toast.success(next ? 'Automation is live.' : 'Automation paused.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not change status.');
    }
  };

  const handleRunNow = async () => {
    try {
      await adminAutomationsService.runNow(id);
      toast.success('Run queued. Refresh in a moment to see it in the history.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not queue a run.');
    }
  };

  const handleTest = async () => {
    const email = testEmail.trim();
    if (!email) return;
    try {
      await adminAutomationsService.test(id, email);
      toast.success(`Test sent to ${email}.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send the test.');
    }
  };

  const handleDelete = async () => {
    try {
      await adminAutomationsService.remove(id);
      toast.success('Automation deleted.');
      navigate('/admin/subscribers/automations');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete automation.');
    }
  };

  if (loading) {
    return (
      <AppShell mode="parent">
        <div className="py-12 text-center text-or-slate-500">Loading…</div>
      </AppShell>
    );
  }

  if (!source) {
    return (
      <AppShell mode="parent">
        <div className="py-12 text-center text-or-slate-500">Automation not found.</div>
      </AppShell>
    );
  }

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <AdminSectionTabs />
        <SubscribersSubNav />

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/subscribers/automations')}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-or-slate-200 bg-white text-or-slate-600 hover:bg-or-slate-50"
            aria-label="Back to automations"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </button>
          <div className="flex-1">
            <div className="or-kicker text-or-orange-600">Automation</div>
            <h1 className="mt-0.5 font-display text-[22px] font-bold text-or-slate-900 sm:text-[26px]">
              {source.name}
            </h1>
          </div>
          <div className="flex items-center gap-2.5">
            {source.enabled ? <Chip variant="live">Live</Chip> : <Chip variant="default">Paused</Chip>}
            <Toggle
              checked={source.enabled}
              onChange={handleToggleEnabled}
              label="Automation enabled"
            />
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="flex flex-col gap-6">
            {/* ---- settings ---- */}
            <section className="flex flex-col gap-5 rounded-2xl border border-or-slate-200 bg-white p-6">
              <label className={labelClass}>
                Name
                <input
                  value={name}
                  onChange={(e) => markDirty(setName)(e.target.value)}
                  maxLength={120}
                  className={inputClass}
                />
              </label>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className={labelClass}>
                  List
                  <select
                    value={listId}
                    onChange={(e) => markDirty(setListId)(e.target.value)}
                    className={inputClass}
                  >
                    {lists.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} ({l.memberCount})
                      </option>
                    ))}
                  </select>
                </label>

                <label className={labelClass}>
                  Template
                  <select
                    value={templateId}
                    onChange={(e) => markDirty(setTemplateId)(e.target.value)}
                    className={inputClass}
                  >
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.format.toUpperCase()})
                      </option>
                    ))}
                  </select>
                </label>

                <label className={labelClass}>
                  Send at
                  <select
                    value={sendHour}
                    onChange={(e) => markDirty(setSendHour)(Number(e.target.value))}
                    className={inputClass}
                  >
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h}>
                        {h % 12 === 0 ? 12 : h % 12}
                        {h < 12 ? 'am' : 'pm'}
                      </option>
                    ))}
                  </select>
                </label>

                <label className={labelClass}>
                  Timezone
                  <select
                    value={timezone}
                    onChange={(e) => markDirty(setTimezone)(e.target.value)}
                    className={inputClass}
                  >
                    {timezoneOptions().map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className={`${labelClass} max-w-[200px]`}>
                Max recipients per run
                <input
                  type="number"
                  min={1}
                  max={10000}
                  value={maxPerRun}
                  onChange={(e) => markDirty(setMaxPerRun)(Number(e.target.value))}
                  className={inputClass}
                />
              </label>
            </section>

            {/* ---- rules ---- */}
            <section className="flex flex-col gap-4 rounded-2xl border border-or-slate-200 bg-white p-6">
              <div>
                <h2 className="font-display text-[16px] font-bold text-or-slate-900">Rules</h2>
                <p className="mt-1 text-[12px] text-or-slate-600">
                  All rules must match. A subscriber receives this automation at most once, ever.
                </p>
              </div>

              <AutomationConditionRows
                schema={schema}
                conditions={conditions}
                onChange={(next) => {
                  setConditions(next);
                  setDirty(true);
                }}
              />

              <div className="flex items-start gap-3 rounded-xl border border-or-slate-200 bg-or-slate-50/60 px-3 py-3">
                <Toggle
                  checked={includeNoAccount}
                  onChange={markDirty(setIncludeNoAccount)}
                  disabled={usesAccountFields}
                  label="Include subscribers without an account"
                />
                <div className="text-[12px] text-or-slate-600">
                  <span className="font-semibold text-or-slate-800">
                    Include subscribers without an account
                  </span>
                  <p className="mt-0.5">
                    {usesAccountFields
                      ? 'Unavailable while any rule tests an account attribute — those subscribers have no account, so they could never match.'
                      : 'Imported and manually-added subscribers are never linked to an account.'}
                  </p>
                </div>
              </div>
            </section>

            {/* ---- run history ---- */}
            <section className="flex flex-col gap-3 rounded-2xl border border-or-slate-200 bg-white p-6">
              <h2 className="font-display text-[16px] font-bold text-or-slate-900">Run history</h2>
              {source.runs.length === 0 ? (
                <p className="py-4 text-center text-[12px] text-or-slate-500">
                  This automation hasn't run yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-[13px]">
                    <thead className="text-[11px] uppercase tracking-[0.08em] text-or-slate-500">
                      <tr>
                        <th className="py-2">Run</th>
                        <th className="py-2">Status</th>
                        <th className="py-2">Recipients</th>
                        <th className="py-2">Finished</th>
                      </tr>
                    </thead>
                    <tbody>
                      {source.runs.map((r) => (
                        <tr
                          key={r.broadcastId}
                          className="cursor-pointer border-t border-or-slate-100 hover:bg-or-slate-50"
                          onClick={() =>
                            navigate(`/admin/subscribers/broadcasts/${r.broadcastId}`)
                          }
                        >
                          <td className="py-2 font-semibold text-or-slate-900">{r.runKey ?? '—'}</td>
                          <td className="py-2">
                            <BroadcastStatusChip status={r.status} />
                          </td>
                          <td className="py-2 text-or-slate-700">{r.recipientCount}</td>
                          <td className="py-2 text-or-slate-400">
                            {r.completedAt ? new Date(r.completedAt).toLocaleString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>

          {/* ---- audience preview ---- */}
          <aside className="flex h-fit flex-col gap-4 rounded-2xl border border-or-slate-200 bg-white p-5 lg:sticky lg:top-6">
            <div>
              <h2 className="font-display text-[16px] font-bold text-or-slate-900">Audience</h2>
              <p className="mt-1 text-[12px] text-or-slate-600">
                Live count from the same query a real run uses.
              </p>
            </div>

            {previewError ? (
              <p className="rounded-lg bg-or-rose-100 px-3 py-2 text-[12px] text-or-rose-700">
                {previewError}
              </p>
            ) : completeConditions.length === 0 ? (
              <p className="text-[12px] text-or-slate-500">Add a rule to see the audience.</p>
            ) : preview ? (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-[32px] font-bold text-or-slate-900">
                    {preview.cappedCount.toLocaleString()}
                  </span>
                  <span className="text-[12px] text-or-slate-500">
                    would receive it{previewing ? ' …' : ''}
                  </span>
                </div>

                <dl className="flex flex-col gap-1 text-[12px] text-or-slate-600">
                  <Row label="Match the rules" value={preview.matchCount} />
                  <Row label="Eligible (not yet sent)" value={preview.eligibleCount} />
                  {preview.alreadySentCount > 0 ? (
                    <Row label="Already sent (permanent)" value={preview.alreadySentCount} />
                  ) : null}
                  {preview.eligibleCount > maxPerRun ? (
                    <p className="mt-1 rounded-lg bg-or-slate-50 px-2.5 py-2 text-or-slate-600">
                      Capped at {maxPerRun.toLocaleString()} per run — the rest roll to the next run.
                    </p>
                  ) : null}
                </dl>

                {preview.samples.length > 0 ? (
                  <div className="flex flex-col gap-1 border-t border-or-slate-100 pt-3">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
                      Sample
                    </span>
                    {preview.samples.map((s) => (
                      <div
                        key={s.subscriberId}
                        className="flex items-center justify-between gap-2 text-[12px]"
                      >
                        <span className="truncate text-or-slate-700">{s.email}</span>
                        {s.alreadySent ? (
                          <span className="shrink-0 text-[11px] text-or-slate-400">sent</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <p className="text-[12px] text-or-slate-500">Calculating…</p>
            )}

            <div className="flex flex-col gap-2 border-t border-or-slate-100 pt-4">
              <Button variant="primary" size="md" onClick={handleSave} disabled={!dirty || saving}>
                {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
              </Button>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={`${inputClass} min-w-0 flex-1`}
                  aria-label="Test send address"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleTest}
                  disabled={!testEmail.trim()}
                >
                  <Send className="h-3.5 w-3.5" aria-hidden />
                  Test
                </Button>
              </div>
              <Button variant="secondary" size="sm" onClick={handleRunNow} disabled={dirty}>
                <Play className="h-3.5 w-3.5" aria-hidden />
                Run now
              </Button>
              <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
                Delete
              </Button>
            </div>
          </aside>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this automation?"
        description="Its rules and its send ledger go too. Past runs stay in the broadcast log."
        confirmLabel="Delete"
        onConfirm={() => {
          setConfirmDelete(false);
          void handleDelete();
        }}
      />
    </AppShell>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between">
      <dt>{label}</dt>
      <dd className="font-semibold text-or-slate-900">{value.toLocaleString()}</dd>
    </div>
  );
}
